import React, { FunctionComponent } from 'react';
import styled from 'styled-components/macro';
import { useMessageGetter } from 'react-message-context';
import { H2, TableHeaderText, TableRowText } from '../../components/basic/Text';
import { DeviceSize } from '../../components/basic/DeviceSize';
import { DebtState } from '../../context/kennoTenantContext';
import { formatDate, replaceDotWithComma } from '../../utils';

const Wrapper = styled.div`
  max-width: 800px;
  padding: 26px 53px;
  margin-bottom: 15px;

  @media ${DeviceSize.desktopS} {
    padding: 0;
  }

  h2 {
    margin-bottom: 26px;
    font-size: 20px;
  }
`;

const HeaderRow = styled.thead`
  > tr > th {
    text-align: right;
    padding-bottom: 12px;
  }

  > tr > th:nth-child(2) {
    padding-right: 17px;
  }

  > tr > th:last-child {
    padding-right: 40px;
    width: 25%;
  }

  @media ${DeviceSize.desktopS} {
    > tr > th:nth-child(2) {
      padding-right: 35px;
    }

    > tr > th:last-child {
      padding-right: 35px;
    }
  }
`;

const TableContainer = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: none;
`;

const DataRow = styled.tr`
  width: 880px;
  height: 40px;

  > .debtamount,
  > .duedate {
    text-align: right;
  }

  > .duedate {
    padding-right: 20px;
  }

  @media ${DeviceSize.desktopS} {
    .debtamount,
    .debttype,
    .duedate {
      font-size: 12px;
    }
  }
`;

const MonthHeaderText = styled(TableHeaderText)`
  text-align: start;
`;

const BoldTd = styled.td`
  font-weight: 700;
`;

const SumRow = styled.tr`
  > td {
    padding-top: 10px;
    letter-spacing: 0.8px;
    padding-bottom: 10px;
  }

  > td:first-child {
    padding-left: 25%;
    text-align: right;
  }

  > td:nth-child(2) {
    text-align: right;
  }

  @media ${DeviceSize.mobileL} {
    > td {
      font-size: 14px;
    }

    > td:first-child {
      padding-left: 15%;
      text-align: left;
    }
  }
`;

export type DebtTableProps = {
  debt: DebtState;
};

export const DebtTable: FunctionComponent<DebtTableProps> = ({ debt }) => {
  const t = useMessageGetter('debt');
  const tMonth = useMessageGetter('months');

  const extractYearAndMonth = (dateString: string): { year: string; month: string } => {
    const [year, month] = dateString.split('-');
    return { year, month };
  };

  const formatYearMonth = (yearMonth: string) => {
    const { year, month } = extractYearAndMonth(yearMonth);
    return `${monthTranslation[month]} ${year}`;
  };

  const monthTranslation: { [key: string]: string } = {
    '01': tMonth('january'),
    '02': tMonth('february'),
    '03': tMonth('march'),
    '04': tMonth('april'),
    '05': tMonth('may'),
    '06': tMonth('june'),
    '07': tMonth('july'),
    '08': tMonth('august'),
    '09': tMonth('september'),
    '10': tMonth('october'),
    '11': tMonth('november'),
    '12': tMonth('december'),
  };

  return (
    <Wrapper>
      <H2>{t('paytype')}</H2>
      <TableContainer>
        <HeaderRow>
          <tr>
            <TableHeaderText>&nbsp;</TableHeaderText>
            <TableHeaderText>{t('debtamount')}</TableHeaderText>
            <TableHeaderText>{t('duedate')}</TableHeaderText>
          </tr>
        </HeaderRow>
        <tbody>
          {debt.data?.debtByMonth.map((month, monthIndex) => (
            <React.Fragment key={monthIndex}>
              <MonthHeaderText>{formatYearMonth(month.yearMonth)}</MonthHeaderText>
              {month.debtByTarget.map((target, index) => (
                <DataRow key={index}>
                  <td className="debttype">
                    <TableRowText>{target.nameOnBill}</TableRowText>
                  </td>
                  <td className="debtamount">
                    <TableRowText>{`${replaceDotWithComma(target.currentDue)} €`}</TableRowText>
                  </td>
                  <td className="duedate">
                    <TableRowText>{formatDate(target.dueDate)}</TableRowText>
                  </td>
                </DataRow>
              ))}
            </React.Fragment>
          ))}
          <SumRow>
            <BoldTd>{t('sum')}</BoldTd>
            <BoldTd>{`${replaceDotWithComma(debt.data?.currentDue)} €`}</BoldTd>
            <td>&nbsp;</td>
          </SumRow>
        </tbody>
      </TableContainer>
    </Wrapper>
  );
};

export default DebtTable;
